import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Segmented } from "antd";
import dayjs from "dayjs";
import {
  StatsViewWrapper,
  StatsGrid,
  StatCard,
  StatValue,
  StatLabel,
  SorterRow,
  SorterAvatar,
  SorterInfo,
  SorterName,
  SorterStat,
  SorterCount,
  LoadingState,
  FieldLabel,
} from "../MailIntakeStyled";
import { fetchStats } from "../../../slice/MailIntakeSlice";

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Last 7 Days" },
  { value: "month", label: "Last 30 Days" },
];

// Scanned mail and manual uploads are one "Mail" bucket on the
// dashboard (Matthew, 2026-07-16); fax and email stay separate.
const SOURCE_LABELS = {
  scanned_mail: "Mail",
  manual_upload: "Mail",
  email_attachment: "Email",
  fax: "Fax",
};

/** Merge same-label sources (Mail = scans + manual uploads), drop
 * zero rows, sort by count. Unknown source codes pass through as-is
 * so a future source is visible instead of silently vanishing. */
export const sourceBreakdown = (bySource) => {
  const merged = {};
  for (const [key, count] of Object.entries(bySource || {})) {
    if (!count) continue;
    const label = SOURCE_LABELS[key] || key;
    merged[label] = (merged[label] || 0) + count;
  }
  return Object.entries(merged).sort((a, b) => b[1] - a[1]);
};

const PIPELINE_LABELS = {
  extracting: "Being read",
  ready: "AI suggestion ready",
  needs_review: "Needs human review",
  failed: "Failed",
  queued: "Waiting to be read",
  not_started: "Waiting to be read",
};

const formatAvgTime = (seconds) => {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

/** Merge "queued"-family buckets for display and drop zero rows. */
/**
 * Trailing-monthly averages for the firm-wide mail block. Skips the
 * current (last, partial) month, months with zero letters, and — for
 * the pages average — months whose upstream page counts predate the
 * mid-Jan 2026 cutover (pages === null, never coerced to zero).
 */
export const firmMailAverages = (monthly) => {
  // Closed months only: prefer the backend's explicit partial flag,
  // fall back to dropping the trailing month for older payloads.
  const rows = monthly || [];
  const closed = rows.some((m) => m.partial != null)
    ? rows.filter((m) => !m.partial)
    : rows.slice(0, -1);
  const withLetters = closed.filter((m) => m.letters > 0);
  // Pages average: only months whose page coverage is essentially
  // complete — a bucket straddling the mid-Jan 2026 cutover carries a
  // real-looking but badly partial sum (pages_missing >> 0).
  const withPages = closed.filter(
    (m) =>
      m.pages != null &&
      m.letters > 0 &&
      (m.pages_missing ?? 0) <= m.letters * 0.05
  );
  const avg = (rowsIn, pick) =>
    rowsIn.length
      ? Math.round(rowsIn.reduce((acc, m) => acc + pick(m), 0) / rowsIn.length)
      : null;
  return {
    avgLetters: avg(withLetters, (m) => m.letters),
    avgPages: avg(withPages, (m) => m.pages),
  };
};

export const pipelineBreakdown = (byPipelineStatus) => {
  const merged = {};
  for (const [key, count] of Object.entries(byPipelineStatus || {})) {
    const label = PIPELINE_LABELS[key];
    if (!label || !count) continue;
    merged[label] = (merged[label] || 0) + count;
  }
  return Object.entries(merged).sort((a, b) => b[1] - a[1]);
};

const StatsView = () => {
  const dispatch = useDispatch();
  const { stats, statsLoading } = useSelector((state) => state.mailIntake);
  const [period, setPeriod] = useState("today");
  // A calendar pick overrides the period buttons; clearing it returns
  // to the selected period.
  const [pickedDate, setPickedDate] = useState(null);

  useEffect(() => {
    dispatch(
      fetchStats({
        period,
        date: pickedDate ? pickedDate.format("YYYY-MM-DD") : "",
      })
    );
  }, [dispatch, period, pickedDate]);

  if (statsLoading && !stats?.summary) {
    return <LoadingState>Loading statistics...</LoadingState>;
  }

  const summary = stats?.summary || {};
  const daily = stats?.daily || [];
  const sorters = [...(stats?.sorters || [])].sort(
    (a, b) => b.docs_processed - a.docs_processed
  );
  const windowLabel = pickedDate
    ? pickedDate.format("MMM D, YYYY")
    : PERIOD_OPTIONS.find((o) => o.value === period)?.label || "Today";

  const received = summary.received_period ?? 0;
  const assigned = summary.assigned_period ?? 0;
  const withSuggestion = summary.assigned_with_suggestion ?? 0;
  const suggestionRate =
    assigned > 0 ? Math.round((withSuggestion / assigned) * 100) : null;
  const bySource = sourceBreakdown(summary.received_by_source);
  const queueBreakdown = pipelineBreakdown(
    summary.queue_now?.by_pipeline_status
  );
  const maxDaily = Math.max(1, ...daily.map((d) => Math.max(d.received, d.assigned)));

  // Firm-wide volume from the SHARED mail table (the stream the old
  // sorter writes) — answers "how much mail does the firm take in?"
  // self-serve (Matthew, 2026-07-21).
  const firmMail = stats?.firm_mail || null;
  const { avgLetters, avgPages } = firmMailAverages(firmMail?.monthly);

  return (
    <StatsViewWrapper>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, color: "#1b2028" }}>
          Mail Statistics · {windowLabel}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Segmented
            value={pickedDate ? null : period}
            onChange={(value) => {
              setPickedDate(null);
              setPeriod(value);
            }}
            options={PERIOD_OPTIONS}
          />
          <DatePicker
            value={pickedDate}
            onChange={setPickedDate}
            allowClear
            placeholder="Pick a day"
            disabledDate={(d) => d && d > dayjs().endOf("day")}
            style={{ fontFamily: "Manrope" }}
          />
        </div>
      </div>

      <StatsGrid>
        <StatCard>
          <StatValue>{received}</StatValue>
          <StatLabel>Mail Received</StatLabel>
          {bySource.length > 0 && (
            <SorterStat style={{ marginTop: 6 }}>
              {bySource.map(([label, n]) => `${label}: ${n}`).join(" · ")}
            </SorterStat>
          )}
        </StatCard>
        <StatCard>
          <StatValue>{assigned}</StatValue>
          <StatLabel>Filed to Matters</StatLabel>
          {suggestionRate !== null && (
            <SorterStat style={{ marginTop: 6 }}>
              {suggestionRate}% filed to the AI-suggested matter
            </SorterStat>
          )}
        </StatCard>
        <StatCard>
          <StatValue>{summary.pending ?? "—"}</StatValue>
          <StatLabel>Waiting to Be Sorted</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{summary.total_queue ?? "—"}</StatValue>
          <StatLabel>Total in Queue Now</StatLabel>
          {queueBreakdown.length > 0 && (
            <SorterStat style={{ marginTop: 6 }}>
              {queueBreakdown
                .map(([label, n]) => `${label}: ${n}`)
                .join(" · ")}
            </SorterStat>
          )}
        </StatCard>
      </StatsGrid>

      {firmMail && (
        <>
          <FieldLabel style={{ margin: "20px 0 12px", fontSize: 13 }}>
            Firm-Wide Mail (all channels, shared mail table)
          </FieldLabel>
          <StatsGrid>
            <StatCard data-testid="firm-letters">
              <StatValue>
                {(firmMail.letters_period ?? 0).toLocaleString()}
              </StatValue>
              <StatLabel>Letters In · {windowLabel}</StatLabel>
            </StatCard>
            <StatCard data-testid="firm-pages">
              <StatValue>
                {firmMail.pages_period != null
                  ? firmMail.pages_period.toLocaleString()
                  : "—"}
              </StatValue>
              <StatLabel>Pages In · {windowLabel}</StatLabel>
            </StatCard>
            <StatCard data-testid="firm-monthly-avg">
              <StatValue>
                {avgLetters != null ? avgLetters.toLocaleString() : "—"}
              </StatValue>
              <StatLabel>Avg Letters / Month</StatLabel>
              {avgPages != null && (
                <SorterStat style={{ marginTop: 6 }}>
                  ≈ {avgPages.toLocaleString()} pages / month
                </SorterStat>
              )}
            </StatCard>
          </StatsGrid>
        </>
      )}

      {daily.length > 1 && (
        <>
          <FieldLabel style={{ margin: "20px 0 12px", fontSize: 13 }}>
            Daily Volume (received vs filed)
          </FieldLabel>
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #edf0f4",
              borderRadius: 10,
              padding: "14px 16px",
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
              minHeight: 120,
            }}
          >
            {daily.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: received ${d.received}, filed ${d.assigned}`}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 2,
                    height: 80,
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "40%",
                      maxWidth: 22,
                      height: `${Math.round((d.received / maxDaily) * 100)}%`,
                      minHeight: d.received > 0 ? 3 : 0,
                      background: "#b9c9e8",
                      borderRadius: "3px 3px 0 0",
                    }}
                  />
                  <div
                    style={{
                      width: "40%",
                      maxWidth: 22,
                      height: `${Math.round((d.assigned / maxDaily) * 100)}%`,
                      minHeight: d.assigned > 0 ? 3 : 0,
                      background: "#4a6ee0",
                      borderRadius: "3px 3px 0 0",
                    }}
                  />
                </div>
                <div style={{ fontSize: 10, color: "#98a6ba" }}>
                  {dayjs(d.date).format("M/D")}
                </div>
              </div>
            ))}
          </div>
          <SorterStat style={{ marginTop: 6 }}>
            Light bars: mail received · Dark bars: mail filed to matters
          </SorterStat>
        </>
      )}

      <FieldLabel style={{ margin: "20px 0 12px", fontSize: 13 }}>
        Sorter Performance
      </FieldLabel>
      <div
        style={{
          background: "#f9fafb",
          border: "1px solid #edf0f4",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 16px",
            borderBottom: "1px solid #edf0f4",
            fontSize: 11,
            fontWeight: 700,
            color: "#98a6ba",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          <div style={{ flex: 1 }}>Sorter</div>
          <div style={{ width: 100, textAlign: "center" }}>Avg Time</div>
          <div style={{ width: 100, textAlign: "center" }}>Docs/Hour</div>
          <div style={{ width: 80, textAlign: "right" }}>Processed</div>
        </div>

        {sorters.length === 0 && (
          <div style={{ padding: 16, fontSize: 13, color: "#98a6ba" }}>
            No documents were filed in this window.
          </div>
        )}
        {sorters.map((sorter) => (
          <SorterRow key={sorter.user_id}>
            <SorterAvatar>
              {(sorter.name || "?")
                .split(" ")
                .map((part) => part[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </SorterAvatar>
            <SorterInfo style={{ flex: 1 }}>
              <SorterName>{sorter.name}</SorterName>
              <SorterStat>{sorter.docs_processed} docs processed</SorterStat>
            </SorterInfo>
            <div
              style={{
                width: 100,
                textAlign: "center",
                fontSize: 13,
                color: "#5a6c87",
              }}
            >
              {formatAvgTime(sorter.avg_seconds_per_doc)}
            </div>
            <div
              style={{
                width: 100,
                textAlign: "center",
                fontSize: 14,
                fontWeight: 600,
                color: "#1b2028",
              }}
            >
              {sorter.docs_per_hour ?? "—"}
            </div>
            <SorterCount>{sorter.docs_processed}</SorterCount>
          </SorterRow>
        ))}
      </div>
    </StatsViewWrapper>
  );
};

export default StatsView;
